import { useState, useRef, useEffect, useMemo, useImperativeHandle } from 'react'
import { getLocale, useLocation } from 'umi'
import moment from 'moment'

import pipe from '@/pages/dashboard/utils/pipeLine'
import {  matchFuzzyRouteUrl } from '@/pages/dashboard/utils/utils'
import {useLocal } from '@/pages/dashboard/utils/customHook'
import { LOCALE_EN } from '../_config/constant'

import useVisible from './useVisible'
import reconciler from '../utils/reconciler'
import { FilterProps } from '../types'
import { MATCH_ROUTE_REG } from '@/pages/dashboard/_config/constant'
import { loadFilterList } from '@/pages/dashboard/services/dashboard'
import { listFetchInterceptor } from '@/pages/dashboard/_config/filterListMap'
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint'


const getName = (target, enKey, chKey) => {
  return getLocale() === LOCALE_EN ? target[enKey] : target[chKey] || target[enKey]
}

const getNameList = (list) => {
  return list?.map((item) => {
    return {
      ...item,
      text: getName(item, 'enText', 'chText'),
    }
  })
}

// 下拉列表数据缓存
let filterMapCache: any = {}
// 基础维度映射关系
let dimensionNameMap: any = {}

const useFilter = (props: FilterProps, ref) => {
  const { locale } = useLocal()
  // 默认展示筛选器组件列表
  const defaultInputList = [
    {
      type: 'zone',
      key: 'zone',
      visible: 1,
      options: {
        defaultValue: ['255'],
      },
    },
    {
      type: 'region',
      key: 'region',
      visible: 1,
      options: {
        defaultValue: {
          regions: [],
          markets: ['255'],
          region_type: 'market',
        },
      },
    },
    {
      type: 'channel',
      key: 'channel',
      visible: 1,
      options: {
        defaultValue: ['255'],
      },
    },
    {
      type: 'system',
      key: 'system',
      visible: 1,
      options: {
        defaultValue: ['255'],
      },
    },
  ]
  const {
    value,
    loading: loadingProps = null,
    inputList: inputListOutside = null,
    defaultValue: defaultValueProps,
    timePickerList = [{ type: 'rangeTime', key: 'rangeTime' }],
    onlyTimePicker = false,
    gameCode,
    buttonLogConfig = {
      confirmButtonId: '',
      resetButtonId: '',
      pageId: '',
    },
    lastDataMetric = "",
    onInit,
    // onLoading,
    onConfirm,
    onReset,
  } = props
  const location = useLocation();
  const initFlag = useRef(false)
  const { visibleLoading, filterVisibleList } = useVisible({
    gameCode
  })
  const [latestDate, setLatestDate] = useState({
    date: moment().format('YYYY-MM-DD'),
    week: moment().format('YYYY-MM-DD'),
    month: moment().format('YYYY-MM-DD'),
  })
  const [keyCountryList, setKeyCountryList] = useState([])
  const inPipeOccupying = useRef<any>(false)
  // 筛选器下拉列表数据
  const [filterMap, setFilterMap] = useState<{ [key: string]: any[] }>(filterMapCache?.[location.pathname] ?? {
    zoneList: [],
    regionList: [],
    channelList: [],
    systemList: [],
    langList: [],
    networkTypeList: [],
    mediaChannelList: [],
  })
  const { zoneList, regionList, channelList, systemList, langList, networkTypeList, mediaChannelList } = filterMap
  // 最新筛选器 list 的引用，为解决异步执行顺序导致的无法取到 filterMap 的问题
  const [loading, setLoading] = useState(loadingProps ?? true)
  const [componentLoading, setComponentLoading] = useState(true)
  const inputList = useMemo(() => {
    if (!filterVisibleList) return []
    const routerPath = location.pathname.replace(MATCH_ROUTE_REG, '/')
    const target = filterVisibleList?.page_selector?.find((item) => matchFuzzyRouteUrl(location.pathname,item.path))

    const blackList = ['/ua/noSpend', '/ua/spend', '/competitor/index', '/competitor/index/keyMetrics', '/competitor/index/insight']
    const timePicker = [...timePickerList]
    // ua 和 competitor 页面暂不做处理
    if (blackList.includes(routerPath)) {
      if (inputListOutside && inputListOutside?.length > 0) return [
        ...timePicker,
        ...inputListOutside,
      ]
      return defaultInputList
    }

    const filterList = (() => {
      // 当前游戏的筛选器列表 > 后端默认筛选器列表 > 前端业务代码传入 > 前端默认筛选器列表
      const selectorList = (() => {
        if (target?.filters?.length > 0) return target?.filters?.map?.(item => ({ ...item, type: item?.key }))
        if (onlyTimePicker) return []
        // realtime revenue 跳过后端默认条件
        if (filterVisibleList?.default_selector?.[0]?.filters?.length > 0) return filterVisibleList?.default_selector?.[0]?.filters?.map?.(item => ({ ...item, type: item?.key }))
        if (inputListOutside && inputListOutside?.length > 0) return inputListOutside
        return defaultInputList
      })()
      let result = selectorList
        ?.filter((item) => item.visible > 0 || item.visible === undefined)
        ?.map((item) => {
          const _defaultValue = (() => {
            if (item.default === undefined || item.default === null) {
              return defaultValue?.[item.key]
            }
            if (item.key === 'region') {
              return {
                regions: [],
                markets: item.default,
                region_type: 'market',
              }
            }
            return item.default
          })()
          // 提取外部传入筛选器列表的自定义配置
          const options = inputListOutside?.filter(inputItem => inputItem?.key === item?.key)?.[0]?.options

          const config = {}
          Object.assign(config, {
            type: item.type || '',
            key: item.key || '',
            options: {
              options: filterMap[item.type + 'List'],
              ...(
                item.type === 'region' ? { popularList: keyCountryList } : {}
              ),
              ...(item.options ?? {}),
              ...(options ?? {}),
              defaultValue: _defaultValue,
            },
          })
          return config
        })

      result = [...timePicker, ...(result ?? [])]
      return result
    })()
    // 保存筛选器列表，但不添加时间筛选器
    window.filterVisibleList = filterList?.filter((item) => !['singleTime', 'contrastDay', 'rangeTime']?.includes(item.type))

    return [...filterList]
  }, [
    JSON.stringify(inputListOutside),
    timePickerList,
    filterVisibleList,
    filterMap,
    keyCountryList
  ])

  const defaultValue = useMemo(() => {
    const defaultValue: {
      [key: string]: any
    } = { ...(defaultValueProps ?? {}) }
    // 后端配置默认值覆盖前端
    inputList.forEach((item) => {
      defaultValue[item?.key] = item?.options?.defaultValue ?? defaultValue[item?.key]
      if (item?.key === 'lang') {
        defaultValue.language = item?.options?.defaultValue ?? defaultValue.language
      }
    })

    const allSelectorKeys = ['zone', 'region', 'channel', 'language', 'system']

    Object.keys(defaultValue)?.forEach(key => {
      const hasTheInput = inputList?.some(item => item.key === (key === 'language' ? 'lang' : key))
      if (allSelectorKeys?.includes(key) && !hasTheInput) {
        defaultValue[key] = null
      }
    })

    return defaultValue
  }, [inputList])

  const getFilterListData = (params?: any) => {
    return new Promise((resolve, reject) => {
      const selectListMap = {
        zone: ['zone'],
        region: ['country', 'area'],
        channel: ['channel'],
        system: ['os'],
        lang: ['lang'],
      }
      const result = {}
      let selector_list = []
      inputList.forEach((item) => {
        if (item.type in selectListMap) {
          selector_list = selector_list.concat(selectListMap[item.type])
        }
      })
      loadFilterList(params ? { ...params, last_data_metric: lastDataMetric, } : {
        game_code: gameCode,
        selector_list: selector_list,
        last_data_metric: lastDataMetric,
      }).then((res: any) => {
        const {
          area_list,
          os_list,
          channel_list,
          country_list,
          zone_list,
          lang_list,
          key_country_list,
          timezone_list,
          last_date
        } = res || {}
        const regionList = ((area_list as any) || [])?.map((region: any) => {
          const country = region?.country_set?.map((market: any) => {
            const filterMarket = {
              country_name_abbre: market?.country_code,
              country_name_en: getName(market, 'country_name_en', 'country_name_ch'),
              country_name_cn: market?.country_name_ch,
              enText: market?.country_name_en,
              chText: market?.country_name_ch,
              deviation: market?.deviation
            }
            return filterMarket
          })

          return {
            country,
            region_abbre: region.area_code?.toLowerCase(),
            region_key: region.area_code,
            region_name_cn: region.area_name_ch,
            region_name_en: getName(region, 'area_name_en', 'area_name_ch'),
            enText: region?.area_name_en,
            chText: region?.area_name_ch,
          }
        })
        // 筛选器维度中英文翻译
        listFetchInterceptor(gameCode, res)
        dimensionNameMap = res
        Object.assign(result, {
          keyCountryList: key_country_list?.map((item) => item.country_code),
          zoneList:
            zone_list
              ?.filter((item) => item.zone_code !== '255')
              ?.map((item) => ({
                text: getName(item, 'zone_name_en', 'zone_name_ch'),
                chText: item.zone_name_ch,
                enText: item.zone_name_en,
                value: item.zone_code,
                deviation: item.deviation
              })) ?? [],
          regionList: regionList
            ? (() => {
              // 按字母排序
              const sortByFirstLetter = (list) => {
                const regions = list.sort((a, b) => {
                  return a?.['region_name_en']?.charCodeAt(0) - b?.['region_name_en']?.charCodeAt(0)
                })
                return regions.map((item) => {
                  const newItem = { ...item }
                  if (newItem['country_set']) {
                    newItem['country_set'] = newItem['country_set'].sort((a, b) => {
                      return a?.['country_name_en']?.charCodeAt(0) - b?.['country_name_en']?.charCodeAt(0)
                    })
                  }
                  return newItem
                })
              }
              return sortByFirstLetter(regionList)
            })()
            : [],
          channelList:
            channel_list
              ?.filter((item) => item.channel_id !== '255')
              ?.map((item) => ({
                text: getName(item, 'channel_name_en', 'channel_name_ch'),
                chText: item.channel_name_ch,
                enText: item.channel_name_en,
                value: item.channel_id,
              })) ?? [],
          systemList: os_list
            ?.filter((item) => item.os_id !== '255')
            ?.map((item) => ({
              text: getName(item, 'os_name_en', 'os_name_ch'),
              chText: item.os_name_ch,
              enText: item.os_name_en,
              value: item.os_id,
            })),
          langList: lang_list
            ?.filter((item) => item.lang_code !== '255')
            ?.map((item) => ({
              text: getName(item, 'lang_name_en', 'lang_name_ch'),
              chText: item.lang_name_ch,
              enText: item.lang_name_en,
              value: item.lang_code,
            })),
          timeZoneList: timezone_list ?? [],
          countryList: country_list ?? [],
          latestDate: {
            date: last_date?.daily_date ? moment(last_date?.daily_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
            week: last_date?.weekly_date ? moment(last_date?.weekly_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
            month: last_date?.monthly_date ? moment(last_date?.monthly_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          }
        })   
        resolve(result)
      }).catch((err) => {
          console.log(err)
          reject(err)
        })
    })
  }

  // 使用外部传入的数据初始化
  const dataListInitOutside = (pipeContext) => new Promise((resolve) => {
    const { inputListOutside } = pipeContext?.state

    const listMap = {
      zone: [],
      region: [],
      channel: [],
      system: [],
      networkType: [],
      mediaChannel: [],
      language: [],
      keyCountryList: []
    }
    inputListOutside?.forEach(item => {
      if (item.type in listMap) {
        listMap[item.type] = item?.options?.options
        if (item.type === 'region') {
          listMap.keyCountryList = item?.options?.popularList
        }
      }
    })

    Object.assign(pipeContext.state, {
      zoneList: getNameList(listMap?.zone),
      regionList: listMap?.region?.map((item: any) => {
        return {
          ...item,
          region_name_en: getName(item, 'region_name_en', 'region_name_cn'),
          country: item.country?.map((item) => {
            return {
              ...item,
              country_name_en: getName(item, 'country_name_en', 'country_name_cn'),
            }
          }),
        }
      }),
      channelList: getNameList(listMap?.channel),
      systemList: getNameList(listMap?.system),
      networkTypeList: getNameList(listMap?.networkType),
      mediaChannelList: getNameList(listMap?.mediaChannel),
      langList: getNameList(listMap?.language),
      keyCountryList: listMap?.keyCountryList,
    })
    resolve(pipeContext)
  })

  // 下拉列表数据初始化
  const fetchListData = (pipeContext) =>
    new Promise((resolve) => {
      const { channelList, regionList, systemList, zoneList, langList, latestDate } = pipeContext.state
      // 若存在缓存列表则不需要再次请求
      // 缓存列表存在, 但是 lastDataMetric 发生了改变, 需要重新请求. (selector接口新增 last_data_metric)
      if (!filterMapCache?.[location.pathname]) {
        getFilterListData()
          .then((result: any) => {
            Object.assign(pipeContext.state, {
              ...(result ?? {})
            })
            filterMapCache[location.pathname] = { ...(result ?? {}), lastDataMetric }
            resolve(pipeContext)
          })
          .catch(() => {
            resolve(pipeContext)
          })
      } else {
        // ua页面回到其他页面重新设置筛选器维度映射关系
        listFetchInterceptor(gameCode, dimensionNameMap)
        Object.assign(pipeContext.state, {
          zoneList: getNameList(zoneList),
          regionList: regionList?.map((item) => {
            return {
              ...item,
              region_name_en: getName(item, 'enText', 'chText'),
              country: item.country?.map((item) => {
                return {
                  ...item,
                  country_name_en: getName(item, 'enText', 'chText'),
                }
              }),
            }
          }),
          channelList: getNameList(channelList),
          systemList: getNameList(systemList),
          langList: getNameList(langList),
          latestDate
        })
        resolve(pipeContext)
      }
    })

  // 下拉列表数据初始化
  const listInit = (pipeContext): any => {
    const { inputListOutside } = pipeContext.state
    if (inputListOutside) return dataListInitOutside(pipeContext)
    return fetchListData(pipeContext)
  }

  const filterInit = ({
    zoneList,
    regionList,
    channelList,
    systemList,
    langList,
    inputListOutside,
    keyCountryList,
    latestDate
  }) => {
    pipe([listInit], {
      value,
      zoneList,
      regionList,
      channelList,
      systemList,
      langList,
      inputListOutside,
      inputList,
      keyCountryList,
      latestDate
    })
      .then((pipeContext: any) => {
        const { state } = pipeContext
        const {
          zoneList,
          regionList,
          channelList,
          systemList,
          networkTypeList,
          mediaChannelList,
          langList,
          keyCountryList,
          latestDate
        } = state

        const filterListMap = {
          zoneList: zoneList ?? [],
          regionList: regionList ?? [],
          channelList: channelList ?? [],
          systemList: systemList ?? [],
          networkTypeList: networkTypeList ?? [],
          mediaChannelList: mediaChannelList ?? [],
          langList: langList ?? [],
        }
        // 筛选器初始化钩子
        if (!initFlag.current) {
          initFlag.current = true
          onInit?.({
            filterListMap,
            defaultValue,
            // loading: false,
          })
          // setComponentLoading(false)
        }
        setLoading(false)
        // 延迟显示筛选器组件，避免出现继承情况下的 value 闪烁
        // setTimeout(() => {
        //   setComponentLoading(false)
        // }, 700) 
        // 筛选器的下拉列表，若 filterMapCache 存在则不需要再次请求 selector 接口
        setFilterMap(filterListMap)
        setKeyCountryList(keyCountryList)
        setLatestDate(latestDate)

        // 调度相关参数
        inPipeOccupying.current = false
        reconciler.popCallback()
      })
      .catch((e) => {
        console.log(e)
        inPipeOccupying.current = false
        reconciler.popCallback()
      })
  }

  useImperativeHandle(ref, () => ({
    loading,
    filterMap,
    keyCountryList, //关键国家
    defaultInputList,//默认筛选器列表
    filterVisibleList,//默从后端获取的筛选器显示隐藏的列表
    getFilterListData, //筛选器前置功能，外部调用接口获取数据，用于筛选器列表展示（如：多时区）
    getNameList, //获取下拉列表每一项的名字
    getName //获取下拉列表单项的名称
  }))

  useEffect(() => {
    // onLoading?.(loading || componentLoading)
  }, [loading, componentLoading])

  useEffect(() => {
    if (visibleLoading) return
    if (loadingProps !== null && loadingProps) {
      // loading 由外部控制时，loadingProps 结束才能初始化筛选器
      // onLoading?.(1)
      return
    }
    // 没有加载到筛选器列表不触发初始化
    if (!filterVisibleList) return
    const { zoneList, regionList, channelList, systemList, langList, keyCountryList, latestDate } = filterMapCache?.[location.pathname] ?? {}
    const params = {
      zoneList: zoneList,
      regionList: regionList,
      channelList: channelList,
      systemList: systemList,
      langList: langList,
      inputListOutside,
      keyCountryList,
      latestDate
    }

    // 增加管道调度功能，当前管道被占用时，待所有 handler 处理完毕再执行下一次管道

    if (inPipeOccupying.current) {
      // setTimeout(() => {
      reconciler.pushCallback(() => filterInit(params))
      // })
      return
    }
    inPipeOccupying.current = true
    filterInit(params)
  }, [
    filterVisibleList,
    visibleLoading,
    inputListOutside,
    loadingProps,
    loading,
    locale
  ])

  // 打点
  const setBtnPointLog = (value, isReset = false) => {
    const { confirmButtonId, resetButtonId, pageId } = buttonLogConfig;
    const logObj = {
      buttonId: isReset ? resetButtonId : confirmButtonId,
      extInfo: JSON.stringify(value),
      pageId,
      typeId: 'dashboard_pc_v2',
    };
    (confirmButtonId || resetButtonId) && buriedBtnPoint(logObj);
  }

  const onFilterConfirm = (value) => {
    setBtnPointLog(value);
    onConfirm && onConfirm(value)
  }

  const onFilterReset = (value) => {
    setBtnPointLog(value, true);
    onReset && onReset(value)
  }

  return {
    loading,
    latestDate,
    componentLoading,
    defaultValue,
    inputList,
    // zoneList,
    // regionList,
    // channelList,
    // systemList,
    // langList,
    // networkTypeList,
    // mediaChannelList,
    keyCountryList,
    onFilterConfirm,
    onFilterReset,
  }
}

export default useFilter
